// @flow
import React from 'react';
import styled from '@emotion/styled';
import { colors } from '@atlaskit/theme';
import { borderRadius, grid } from '../constants';
import type { Quote, AuthorColors } from '../types';
import type { DraggableProvided } from 'react-beautiful-dnd';
import CardEdit from "./CardEdit";
import likeImg from "../assets/like.png";
import clapImg from "../assets/clap.png";
import heartImg from "../assets/heart.png";
import ideaImg from "../assets/idea.png";
import deleteImg from "../assets/delete.png";
import editingImg from "../assets/editing.png";
import celebrateImg from "../assets/celebrate.png";
import "./../css/board.css";
import CardService from "./../../services/card.js";
import { confirmDialogA11y } from "./../../util/confirmDialogA11y.jsx";
import 'react-confirm-alert/src/react-confirm-alert.css';
import { announce } from "./../../util/liveRegion.js";
import "./../css/tooltip.css";

type Props = {
  card: Quote,
  isDragging: boolean,
  provided: DraggableProvided,
  isClone?: boolean,
  isGroupedOver?: boolean,
  style?: Object,
  index?: number,
};

const oneColor = {
  "soft": "#E3FCEF",
  "hard": "rgba(9, 30, 66, 0.71)"
};

const getBackgroundColor = (
  isDragging: boolean,
  isGroupedOver: boolean,
  authorColors: AuthorColors,
) => {
  // if (isDragging) {
  //   return authorColors.soft;
  // }

  if (isGroupedOver) {
    return "#f7f5f4";
  }

  return colors.N0;
};

const getBorderColor = (isDragging: boolean, authorColors: AuthorColors) =>
  isDragging ? authorColors.hard : 'transparent';

const imageSize: number = 40;

const SideButtons = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px 6px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px) saturate(140%);
  -webkit-backdrop-filter: blur(6px) saturate(140%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
 `;

const Container = styled.article`
  position: relative;
  border-radius: ${borderRadius}px;
  border: 2px solid transparent;
  background-color: ${(props) =>
    getBackgroundColor(props.isDragging, props.isGroupedOver, props.colors)};

  box-sizing: border-box;
  padding: ${grid}px;
  min-height: ${imageSize}px;
  margin-bottom: 15px;
  user-select: none;
  box-shadow: 1px 2px 5px 0px rgb(0 0 0 / 4%);
  color: ${colors.N900};

  &:hover,
  &:active {
    color: ${colors.N900};
    text-decoration: none;
  }

  &:focus {
    outline: 2px solid ${(props) => props.colors.hard};
    outline-offset: 2px;
  }

  /* flexbox */
  display: block;
`;

const Content = styled.div`
  /* flex child */
  flex-grow: 1;

  /*
    Needed to wrap text in ie11
    https://stackoverflow.com/questions/35111090/why-ie11-doesnt-wrap-the-text-in-flexbox
  */
  flex-basis: 100%;

  /* flex parent */
  display: flex;
  flex-wrap: no-wrap;
 `;

const CardText = styled.div`
  font-family: 'Segoe UI Regular';
  font-weight: 500;
  flex: 1;
  white-space: pre-wrap;
`;

const Divider = styled.div`
  border-top: solid 1px #ebebeb;
  border-radius: 100px;
  display:block;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const TagsContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
`;

const Tag = styled.div`
  background-color: ${props => props.color ? props.color : "blue"};
  font-family: 'Segoe UI Regular';
  display: inline-block;
  border-radius: 5px;
  margin-right: 8px;
  min-width: 50px;
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 5px;
  padding-right: 5px;
  color: white;
  text-align: center
`;

const ReactionsContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  width: 100%;
  margin-left: 2px;
`;

const Reaction = styled.div`
  margin-right: 8%;
  display: inline-block;
`;

const ReactionIcon = styled.img`
  height: ${props => props.height ? props.height : "16"}px;
  display: inline-block;
  cursor: pointer;
  opacity: ${props => props.opacity ? props.opacity : "0.5"};
  transition: all 0.3s ease;

  &:hover {
   opacity: 1;
  }
`;

const ActionIcon = styled.img`
  height: 16px;
  padding-top:4px;
  display: inline-block;
  opacity: 0.5;
  transition: all 0.2s ease;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  & img {
    opacity: 0.5;
    transition: opacity 0.2s ease;
  }

  &:hover img,
  &:focus img {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid #0052CC;
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

const MoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  font-weight: 600;
  color: #5c6263;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  padding: 2px 4px;
  display: inline-flex;
  align-items: center;
  font-family: inherit;

  &:hover,
  &:focus {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid #0052CC;
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

const ReactionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-right: 8%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  & img {
    transition: opacity 0.3s ease;
  }

  &:hover img,
  &:focus img {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid #0052CC;
    outline-offset: 2px;
    border-radius: 3px;
  }
`;


const ReactionVotes = styled.span`
   position: relative;
   bottom: 4px;
   left: 5px;
   font-weight: 500;
`;

function getStyle(provided: DraggableProvided, style: ?Object) {
  if (!style) {
    return provided.draggableProps.style;
  }

  return {
    ...provided.draggableProps.style,
    ...style,
  };
}

// Previously this extended React.Component
// That was a good thing, because using React.PureComponent can hide
// issues with the selectors. However, moving it over does can considerable
// performance improvements when reordering big lists (400ms => 200ms)
// Need to be super sure we are not relying on PureComponent here for
// things we should be doing in the selector as we do not know if consumers
// will be using PureComponent
export default class Card extends React.PureComponent {
  constructor() {
    super();
    const card = this.props?.card || null;
    this._cardTextRef = React.createRef();
    this._reactionRefs = [];
    this._editButtonEl = null;

    this.state = {
      contentHeight: null,
      // Below fields are used to save edit version of the card
      isEditing: false,
      content: null,
      tags: null,
      reactions: {
        like: card != null ? card?.votes?.like?.length : 0,
        clap: card != null ? card?.votes?.clap?.length : 0,
        love: card != null ? card?.votes?.love?.length : 0,
        awesome: card != null ? card?.votes?.awesome?.length : 0,
        insightful: card != null ? card?.votes?.insightful?.length : 0,

      }
    }
  }

  onReactionKeyDown = (e, i, total) => {
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % total;
    else if (e.key === 'ArrowLeft') next = (i - 1 + total) % total;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = total - 1;
    if (next === null) return;
    e.preventDefault();
    const target = this._reactionRefs[next];
    if (target) target.focus();
  }

  onCardEditButtonClick = () => {
    const measuredHeight = this._cardTextRef.current?.clientHeight ?? 0;
    const contentHeight = Math.max(120, measuredHeight);
    this.setState({isEditing: true, contentHeight});
  }

  onCardDeleteButtonClick = async () => {
    confirmDialogA11y({
      title: 'You sure about deleting this card?',
      buttons: [
        { label: 'No', onClick: () => {} },
        { label: 'Yes', onClick: async () => await this.deleteCard() }
      ]
    });
  }

  deleteCard = async () => {
    const {card} = this.props;
    const snippet = (card?.content ?? '').slice(0, 40);
    await CardService.delete(card.boardId, card.columnId, card.cardId);
    announce(`Card deleted${snippet ? `: ${snippet}` : ''}`);
  }


  focusEditButtonAfterRender = () => {
    requestAnimationFrame(() => {
      if (this._editButtonEl && typeof this._editButtonEl.focus === 'function') this._editButtonEl.focus();
    });
  }

  onCardEditSuccess = (newContent, newTags) => {
    this.setState({content: newContent, tags: newTags, isEditing: false}, this.focusEditButtonAfterRender);
    announce(`Card updated`);
  }

  onCardEditCancel = () => {
    this.setState({isEditing: false, content: null, newTags: null}, this.focusEditButtonAfterRender);
  }

  onMoveCardClick = (direction) => {
    const {card, onMoveCard} = this.props;
    if (!onMoveCard || !card) return;
    onMoveCard(card.columnId, card.cardId, direction);
  }

  onVote = async (votingFieldName) => {
    if (!votingFieldName) {
      return;
    }
    const {card} = this.props;
    await CardService.upvoteCard(card.boardId, card.columnId, card.cardId, votingFieldName);

    const labels = { like: 'Like', clap: 'Clap', love: 'Love', awesome: 'Celebrate', insightful: 'Insightful' };
    const newCount = this.state.reactions[votingFieldName] + 1;
    switch(votingFieldName) {
      case 'like': this.setState({reactions: {...this.state.reactions, like: this.state.reactions.like + 1}}); break;
      case 'clap': this.setState({reactions: {...this.state.reactions, clap: this.state.reactions.clap + 1}}); break;
      case 'love': this.setState({reactions: {...this.state.reactions, love: this.state.reactions.love + 1}}); break;
      case 'awesome': this.setState({reactions: {...this.state.reactions, awesome: this.state.reactions.awesome + 1}}); break;
      case 'insightful': this.setState({reactions: {...this.state.reactions, insightful: this.state.reactions.insightful + 1}}); break;
    }
    announce(`${labels[votingFieldName]} — ${newCount} ${newCount === 1 ? 'person has' : 'people have'} reacted to this card.`);
  }
  
  // We sync the state.reactions with the props.card.reactions. We do this so we can show reaction updates
  // immediatly after clicking instead of waiting for server respone. 
  // This lifecylcle method will update the state whenvver the props.card.reactions change.
  componentWillReceiveProps(nextProps) {
    // Any time nextProps?.card?.votes changes, update state.
    // console.log("props updated", nextProps.card, this.props.card);
    if ((nextProps?.card?.votes?.like?.length !== this.props.card?.votes?.like?.length) || 
    (nextProps?.card?.votes?.clap?.length !== this.props.card?.votes?.clap?.length) || 
    (nextProps?.card?.votes?.love?.length !== this.props.card?.votes?.love?.length) || 
    (nextProps?.card?.votes?.awesome?.length !== this.props.card?.votes?.awesome?.length) || 
    (nextProps?.card?.votes?.insightful?.length !== this.props.card?.votes?.insightful?.length)) {
      this.setState({
        reactions: {
          like: nextProps?.card?.votes?.like?.length || 0,
          clap: nextProps?.card?.votes?.clap?.length || 0,
          love: nextProps?.card?.votes?.love?.length || 0,
          awesome: nextProps?.card?.votes?.awesome?.length || 0,
          insightful: nextProps?.card?.votes?.insightful?.length || 0,
        }
      });
    }
  }

  // when the component loads at first, the state is not getting loaded with the correct votes, only after user make a vote, the state starts to sync
  // this is why if the state values are all 0, we'll depend on the card informaiton to show the current votes
  isReactionStateStale = (reactions) => {
    return (reactions.like === 0 && reactions.clap === 0 && reactions.awesome === 0 && reactions.insightful === 0 && reactions.love === 0 );
  }

  
  render() {
    const {
      card,
      isDragging,
      isGroupedOver,
      provided,
      style,
      isClone,
      index
    } = this.props;

    const {isFirstCard, isLastCard, isFirstColumn, isLastColumn, isFilterActive, previousColumnTitle, nextColumnTitle, columnTitle} = this.props;
    const cardContent = this.state.content ?? card.content ?? '';
    const cardSummary = cardContent.length > 50 ? `${cardContent.slice(0, 50).trim()}…` : cardContent;
    const cardLabel = cardSummary ? `card: ${cardSummary}` : 'card';
    const canMove = !isFilterActive;

    if (this.state.isEditing) {
      return <CardEdit 
        onUpdateSuccess={this.onCardEditSuccess} 
        onCancel={this.onCardEditCancel} 
        {...this.props}
        // contentHeight help give inital height to textArea in edit mode
        contentHeight={this.state.contentHeight} 
      />
    }
    // in edit mode, we update state.tags so user can see his new tags instantly. Otherwise use props tags
    const tags = this.state.tags ?? card?.tags;
     return (
      <div className="cardWrapper" role="listitem">
      <Container
        isDragging={isDragging}
        isGroupedOver={isGroupedOver}
        colors={oneColor}
        isClone={isClone}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        role={undefined}
        tabIndex={undefined}
        aria-describedby={undefined}
        style={getStyle(provided, style)}
        data-is-dragging={isDragging}
        data-testid={card.id}
        data-index={index}
      >
        <Content>
           <CardText ref={this._cardTextRef}>
              {columnTitle && <span className="sr-only">In '{columnTitle}': </span>}
              {this.state.content ?? card.content}
           </CardText>
           <SideButtons className="editButton">
              {canMove && !isFirstColumn && (
                <MoveButton
                  type="button"
                  aria-label={previousColumnTitle ? `Move this card to ${previousColumnTitle}` : 'Move this card to the previous column'}
                  title={previousColumnTitle ? `Move to ${previousColumnTitle}` : 'Move to previous column'}
                  onClick={() => this.onMoveCardClick('previousColumn')}
                >
                  <span aria-hidden="true">&larr;</span>
                </MoveButton>
              )}
              {canMove && !isLastColumn && (
                <MoveButton
                  type="button"
                  aria-label={nextColumnTitle ? `Move this card to ${nextColumnTitle}` : 'Move this card to the next column'}
                  title={nextColumnTitle ? `Move to ${nextColumnTitle}` : 'Move to next column'}
                  onClick={() => this.onMoveCardClick('nextColumn')}
                >
                  <span aria-hidden="true">&rarr;</span>
                </MoveButton>
              )}
              <IconButton type="button" aria-label="Edit this card" title="Edit this card" onClick={this.onCardEditButtonClick} ref={(el) => { this._editButtonEl = el; }}>
                <ActionIcon src={editingImg} alt="" aria-hidden="true" />
              </IconButton>
              <IconButton type="button" aria-label="Delete this card" title="Delete this card" onClick={this.onCardDeleteButtonClick}>
                <ActionIcon src={deleteImg} alt="" aria-hidden="true" />
              </IconButton>
           </SideButtons>
        </Content>
        <TagsContainer>
          {
            tags?.map((tag, index) => <Tag key={`${tag.text}${index}${tag.color}`} color={tag.color}>{tag.text}<span className="sr-only">, tag</span></Tag>)
          }
        </TagsContainer>
        <Divider />
        {(() => {
          const stale = this.isReactionStateStale(this.state.reactions);
          const reactions = [
            { key: 'like', label: 'Like', img: likeImg, height: '19', count: stale ? card.votes.like.length : this.state.reactions.like },
            { key: 'clap', label: 'Clap', img: clapImg, height: '20', count: stale ? card.votes.clap.length : this.state.reactions.clap },
            { key: 'love', label: 'Love', img: heartImg, height: '18', count: stale ? card.votes.love.length : this.state.reactions.love },
            { key: 'awesome', label: 'Celebrate', img: celebrateImg, height: '20', count: stale ? card.votes.awesome.length : this.state.reactions.awesome },
            { key: 'insightful', label: 'Insightful', img: ideaImg, height: '22', count: stale ? card.votes.insightful.length : this.state.reactions.insightful },
          ];
          const totalVotes = reactions.reduce((sum, r) => sum + r.count, 0);
          const toolbarLabel = totalVotes === 0
            ? 'Reactions — no one has reacted yet'
            : `Reactions — ${totalVotes} ${totalVotes === 1 ? 'person has' : 'people have'} reacted to this card`;
          return (
            <ReactionsContainer role="group" aria-label={toolbarLabel}>
              {reactions.map((r, i) => {
                const countText = r.count === 0
                  ? 'no one yet'
                  : `${r.count} ${r.count === 1 ? 'person' : 'people'}`;
                return (
                  <ReactionButton
                    key={r.key}
                    type="button"
                    ref={(el) => (this._reactionRefs[i] = el)}
                    aria-label={`${r.label} — ${countText}`}
                    title={`${r.label} (${r.count})`}
                    onKeyDown={(e) => this.onReactionKeyDown(e, i, reactions.length)}
                    onClick={() => this.onVote(r.key)}
                  >
                    <ReactionIcon opacity="0.4" height={r.height} alt="" aria-hidden="true" src={r.img} />
                    <ReactionVotes aria-hidden="true">{r.count}</ReactionVotes>
                  </ReactionButton>
                );
              })}
            </ReactionsContainer>
          );
        })()}

      </Container>
      </div>
    );
  }
}

export const ViewOnlyCard = (props) => {
  const {card, tags} = props;
  return (<Container
    isDragging={false}
    isGroupedOver={false}
    colors={oneColor}
  >
    <Content>
       <CardText>
          {card.content}
       </CardText>
    </Content>
    <TagsContainer>
      {
        tags?.map((tag, index) => <Tag key={`${tag.text}${index}${tag.color}`} color={tag.color}>{tag.text}</Tag>)
      }
    </TagsContainer>
    <Divider />
    <ReactionsContainer>
      <Reaction aria-label={`Like, ${card?.votes?.like?.length ?? 0} votes`}><ReactionIcon opacity="0.4" height="19" alt="" aria-hidden="true" src={likeImg} /><ReactionVotes aria-hidden="true">{card?.votes?.like?.length}</ReactionVotes></Reaction>
      <Reaction aria-label={`Clap, ${card?.votes?.clap?.length ?? 0} votes`}><ReactionIcon opacity="0.4" height="20" alt="" aria-hidden="true" src={clapImg} /><ReactionVotes aria-hidden="true">{card?.votes?.clap?.length}</ReactionVotes></Reaction>
      <Reaction aria-label={`Love, ${card?.votes?.love?.length ?? 0} votes`}><ReactionIcon opacity="0.4" height="18" alt="" aria-hidden="true" src={heartImg} /><ReactionVotes aria-hidden="true">{card?.votes?.love?.length}</ReactionVotes></Reaction>
      <Reaction aria-label={`Celebrate, ${card?.votes?.awesome?.length ?? 0} votes`}><ReactionIcon opacity="0.4" height="20" alt="" aria-hidden="true" src={celebrateImg} /><ReactionVotes aria-hidden="true">{card?.votes?.awesome?.length}</ReactionVotes></Reaction>
      <Reaction aria-label={`Insightful, ${card?.votes?.insightful?.length ?? 0} votes`}><ReactionIcon opacity="0.4" height="22" alt="" aria-hidden="true" src={ideaImg} /><ReactionVotes aria-hidden="true">{card?.votes?.insightful?.length}</ReactionVotes></Reaction>
    </ReactionsContainer>

  </Container>);
}